import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useReactToPrint } from 'react-to-print';
import { IconButton, Tooltip } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { useRollbar } from '@rollbar/react';
import Toast, { useToast } from '../../components/useToast';
import { createFirstHxReportHtml, firstHxDocumentStyles } from '../../utils/reportFormatter';
import { axiosErrorToUserMessage, axiosIntakeInstance } from '../../services/axios';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';

export default function PrintReportButton({
  htmlContent, mkey, activeSiteId, patientID, customLogoPath, siteName,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const intakeAxiosWithAuth = useAxiosPrivate(axiosIntakeInstance);
  const {
    toastState, toastSetSuccessNotification, toastSetErrorNotification, toastOnClose,
  } = useToast();
  const reportRef = useRef(null);
  const { rollbar } = useRollbar();

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `FirstHx Report - ${patientID || 'Patient'}`,
    onAfterPrint: () => {
      toastSetSuccessNotification('Report printed successfully');
      setIsLoading(false);
    },
    onPrintError: () => {
      toastSetErrorNotification('An error occurred while printing');
      setIsLoading(false);
    },
    pageStyle: `
      @media print {
        .print-source {
          display: block !important;
        }
        @page {
          margin: 0;
          size: A4;
        }
      }
      ${firstHxDocumentStyles}
    `,
  });

  const onPrintClick = async () => {
    try {
      setIsLoading(true);
      // If html content doesn't exist, fetch it from the server
      let reportContent = htmlContent;
      if (!reportContent && mkey && activeSiteId) {
        const response = await intakeAxiosWithAuth.get(`/${activeSiteId}/${mkey}/review`);
        reportContent = response.data.reportHtml;
      }

      if (!reportContent || reportContent.trim() === '') {
        toastSetErrorNotification('No report content available to print');
        setIsLoading(false);
        return;
      }

      // Update the content in the hidden div
      if (reportRef.current) {
        // Pass custom logo directly - it will be null if not enabled
        reportRef.current.innerHTML = createFirstHxReportHtml(
          reportContent,
          patientID,
          customLogoPath,
          siteName,
        );
      }
      handlePrint();
    } catch (error) {
      toastSetErrorNotification(
        axiosErrorToUserMessage(error) || 'An error occurred when printing the report.',
      );
      rollbar.warn('Error printing report', error);
      setIsLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Print report" arrow>
        <IconButton onClick={onPrintClick} disabled={isLoading}>
          <PrintIcon />
        </IconButton>
      </Tooltip>
      <div
        ref={reportRef}
        className="print-source"
        style={{ display: 'none' }}
      />
      <Toast
        open={toastState.open}
        severity={toastState.severity}
        text={toastState.text}
        onClose={toastOnClose}
      />
    </>
  );
}

PrintReportButton.propTypes = {
  htmlContent: PropTypes.string,
  mkey: PropTypes.string.isRequired,
  activeSiteId: PropTypes.string.isRequired,
  patientID: PropTypes.string,
  customLogoPath: PropTypes.string,
  siteName: PropTypes.string,
};

PrintReportButton.defaultProps = {
  htmlContent: '',
  patientID: '',
  customLogoPath: null,
  siteName: null,
};
